import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import EmailSignUp from 'layout/footer/EmailSignUp'
import {
  createLinkQaAttribute,
  createSectionHeaderQaAttribute,
  createSectionQaAttribute,
  FOOTER_EMAIL_SIGNUP_COLUMNS,
  FOOTER_GRID_COLUMN_GAP,
  FOOTER_GRID_COLUMNS,
  FOOTER_LINK_COLUMNS,
  getFooterLinkUrl,
} from 'layout/footer/footerUtils'
import OneTrustLink from 'layout/footer/OneTrustLink'
import { t } from 'localization'
import { footerNavigationTrackEvent } from 'mParticle/footerTrackEvents'
import CurrencySelectButton from 'shared/components/Storytelling/CurrencySelectButton'
import { defaultFooter } from 'shared/data/defaultBreadcrumbs'
import { useUser } from 'shared/hooks/useUser'
import { FlightClubLogoIcon } from 'shared/icons/FlightClubLogo'
import { colors, mediaQueries } from 'shared/theme'
import { Breadcrumb } from 'shared/types/Breadcrumb'

const TP = 'layout.footer.Footer'

type NewFooterProps = {
  footerData: Breadcrumb
}

/**
 * Footer layout behind the TEMP_WEB_FC_ENABLE_NEW_FOOTER flag.
 */
const NewFooter = ({ footerData }: NewFooterProps) => {
  const tv = useTranslation()
  const { isAuthenticated } = useUser()
  const footer = footerData?.children?.length ? footerData : defaultFooter
  const sectionLinks = footer?.children?.find((crumb) => crumb.slug === 'footer/sectionLinks')
  const legalLinks = footer?.children?.find((crumb) => crumb.slug === 'footer/legalLinks')

  return (
    <Container role="contentinfo">
      <TopSection>
        <LinkColumns>
          {sectionLinks?.children?.map((section) => (
            <LinkColumn data-qa={createSectionQaAttribute(section.name)} key={section.slug}>
              <ColumnHeading>
                {section.urlOverride ? (
                  <Link
                    data-qa={createSectionHeaderQaAttribute(section.name)}
                    href={getFooterLinkUrl(section, section, isAuthenticated)}
                    onClick={() =>
                      footerNavigationTrackEvent({
                        linkType: 'section_header',
                        sectionName: section.name,
                        url: getFooterLinkUrl(section, section, isAuthenticated),
                        urlName: section.name,
                      })
                    }
                  >
                    {section.name}
                  </Link>
                ) : (
                  section.name
                )}
              </ColumnHeading>
              <ul>
                {section.children?.map((link) => (
                  <li key={link.slug}>
                    <Link
                      data-qa={createLinkQaAttribute(section.name)}
                      href={getFooterLinkUrl(section, link, isAuthenticated)}
                      onClick={() =>
                        footerNavigationTrackEvent({
                          linkType: 'section_link',
                          sectionName: section.name,
                          url: getFooterLinkUrl(section, link, isAuthenticated),
                          urlName: link.name,
                        })
                      }
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </LinkColumn>
          ))}
        </LinkColumns>
        <EmailSignUpContainer>
          <EmailSignUp />
        </EmailSignUpContainer>
      </TopSection>

      <BottomMeta>
        <PillRow>
          <CurrencySelectButton />
        </PillRow>
        <BottomBar>
          <FooterLogoLink
            aria-label={t(`${TP}.logoLabel`, 'FlightClub Home')}
            data-qa="FooterLogo"
            href="/"
          >
            <FlightClubLogoIcon style={{ height: '13px', width: '70.954px' }} />
          </FooterLogoLink>
          <BottomBarInner>
            <Copyright>
              &copy;{' '}
              <Trans
                defaults="{date} Flight Club New York LLC. All Rights Reserved"
                i18nKey={`${TP}.copyright`}
                t={tv.t}
                values={{ date: new Date().getFullYear() }}
              />
            </Copyright>
            <LegalLinks data-qa="FooterLegalLinksSection">
              {legalLinks?.children?.map((link) => (
                <li key={link.slug}>
                  <Link
                    data-qa={createLinkQaAttribute('legalLinks')}
                    href={getFooterLinkUrl(legalLinks, link)}
                    onClick={() =>
                      footerNavigationTrackEvent({
                        linkType: 'legal',
                        sectionName: legalLinks?.name ?? 'legalLinks',
                        url: getFooterLinkUrl(legalLinks, link),
                        urlName: link.name,
                      })
                    }
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <OneTrustLink />
              </li>
            </LegalLinks>
          </BottomBarInner>
        </BottomBar>
      </BottomMeta>
    </Container>
  )
}

/* stylelint-disable no-descending-specificity */
const ColumnHeading = styled.h2`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.25;
  margin: 0;
  text-transform: uppercase;

  &&& a {
    font-size: 12px;
  }
`

const Container = styled.footer`
  background-color: ${colors.black};
  color: ${colors.mediumGray};
  font-size: 16px;
  line-height: 22px;

  a {
    color: ${colors.mediumGray};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  ${mediaQueries.tabletOrLarger} {
    a:hover {
      text-decoration: none;
    }
  }

  ${ColumnHeading} {
    color: ${colors.white};

    a {
      color: ${colors.white};
    }
  }
`

const TopSection = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 80px;
  margin-left: auto;
  margin-right: auto;
  max-width: 1600px;
  padding: 56px 24px 80px;

  ${mediaQueries.tabletOrLarger} {
    padding-left: 40px;
    padding-right: 40px;
  }

  ${mediaQueries.desktop} {
    align-items: start;
    display: grid;
    gap: 40px ${FOOTER_GRID_COLUMN_GAP};
    grid-template-columns: repeat(${FOOTER_GRID_COLUMNS}, 1fr);
  }
`

const LinkColumns = styled.div`
  display: grid;
  gap: 48px 24px;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;

  ${mediaQueries.tabletOrLarger} {
    gap: 40px ${FOOTER_GRID_COLUMN_GAP};
    grid-template-columns: repeat(${FOOTER_LINK_COLUMNS}, 1fr);
  }

  ${mediaQueries.desktop} {
    grid-column: 1 / ${FOOTER_LINK_COLUMNS + 1};
  }

  ${mediaQueries.tabletOrLarger} and (hover: hover) and (pointer: fine) {
    a:hover {
      text-decoration: underline;
    }
  }
`

const LinkColumn = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;

  ul {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0;
    padding: 0;
  }

  li {
    list-style-type: none;
    min-width: 0;
  }

  /* Column links are white; only the legal row and copyright stay muted. */
  a {
    color: ${colors.white};
    display: block;
    font-size: 14px;
    line-height: 1.2;
    overflow-wrap: break-word;
  }
`

const EmailSignUpContainer = styled.section`
  width: 100%;

  ${mediaQueries.desktop} {
    grid-column: -${FOOTER_EMAIL_SIGNUP_COLUMNS + 1} / -1;
    justify-self: end;
    max-width: 340px;
  }
`

const BottomMeta = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  max-width: 1600px;

  ${mediaQueries.tabletOrLarger} {
    align-items: center;
    flex-flow: row wrap;
    justify-content: space-between;
  }
`

const PillRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 24px 80px;

  ${mediaQueries.tabletOrLarger} {
    justify-content: flex-end;
    order: 3;
    padding: 24px 40px;
  }
`

const BottomBar = styled.nav`
  padding: 24px;

  ${mediaQueries.tabletOrLarger} {
    align-items: center;
    display: flex;
    gap: 40px;
    order: 2;
    padding: 24px 40px;
  }

  ${mediaQueries.mobile} {
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 32px;
    padding-bottom: 40px;
    padding-top: 40px;
  }
`

const FooterLogoLink = styled(Link)`
  align-items: center;
  display: flex;
`

const BottomBarInner = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;

  ${mediaQueries.tabletOrLarger} {
    flex-direction: row;
    gap: 24px;
    text-align: left;
  }
`

const Copyright = styled.div`
  font-size: 12px;
  line-height: 1.5;

  ${mediaQueries.tabletOrLarger} {
    order: 2;
  }
`

const LegalLinks = styled.ul`
  display: flex;
  gap: 24px;
  margin: 0;
  padding: 0;

  li {
    list-style-type: none;
  }

  a,
  span {
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
  }

  ${mediaQueries.tabletOrLarger} {
    order: 1;
  }
`

export default NewFooter
